<script setup>
import { computed, ref } from 'vue';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';

const { contentAttributes, createdAt } = useMessageContext();

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, yyyy, h:mm a')
);
const { t } = useI18n();

const isProductsExpanded = ref(false);
const initialProductsCount = 2;

const order = computed(() => {
  const attrs = contentAttributes.value || {};
  return attrs.order || attrs.items?.[0] || attrs;
});

const preText = computed(() => {
  const attrs = contentAttributes.value || {};
  return attrs.pre_text || attrs.preText || '';
});

const orderId = computed(() => {
  return order.value?.order_id || order.value?.orderId || order.value?.id || '';
});

const status = computed(() => order.value?.status || '');

const total = computed(() => {
  return order.value?.total || order.value?.total_amount || '';
});

const invoiceStatus = computed(() => {
  return order.value?.invoice_status || order.value?.invoiceStatus || '';
});

const products = computed(() => {
  return order.value?.products || [];
});

const visibleProducts = computed(() => {
  if (isProductsExpanded.value) return products.value;
  return products.value.slice(0, initialProductsCount);
});

const hasMoreProducts = computed(() => {
  return products.value.length > initialProductsCount;
});

const hiddenProductsCount = computed(() => {
  return products.value.length - initialProductsCount;
});

const formatDate = dateString => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const getOrderDate = value => {
  return formatDate(value?.date || value?.ordered_on || value?.orderedOn);
};

const getStatusBadgeClass = value => {
  const baseClass = 'oc-status-badge';
  return `${baseClass} oc-status-${value?.toLowerCase() || 'default'}`;
};

const getProductSku = product => product?.sku || product?.product_sku || '';
const getProductName = product =>
  product?.name || product?.title || product?.product_name || '';
const getProductQty = product => product?.qty || product?.quantity || '';
const getProductUnitPrice = product =>
  product?.unit_price || product?.unitPrice || product?.price || '';

const toggleProducts = () => {
  isProductsExpanded.value = !isProductsExpanded.value;
};
</script>

<template>
  <div
    class="text-sm dark:bg-n-solid-blue text-n-slate-12 right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm w-full max-w-full sm:max-w-lg overflow-hidden px-3 py-3 shadow-md"
  >
    <div v-if="preText" class="mb-4">
      <p
        class="text-[16px] leading-[20px] font-normal text-[#475569] dark:text-white break-words"
      >
        {{ preText }}
      </p>
    </div>

    <div
      class="bg-n-alpha-3 dark:bg-n-solid-blue border border-n-strong rounded-lg pt-4 pb-3 px-3"
    >
      <div class="flex items-start justify-between gap-2">
        <p class="oc-order-id m-0 break-words">
          {{ t('CONVERSATION.ORDER_CARD.ORDER_PREFIX') }}{{ orderId }}
        </p>
        <span v-if="status" :class="getStatusBadgeClass(status)">
          {{ status }}
        </span>
      </div>

      <div class="mt-2 flex flex-col gap-1">
        <p class="m-0 flex items-center gap-0.5">
          <span class="oc-label-text">
            {{ t('CONVERSATION.ORDER_CARD.DATE') }}
          </span>
          <span class="oc-value-text">
            {{ getOrderDate(order) }}
          </span>
        </p>
        <p class="m-0 flex items-center gap-0.5">
          <span class="oc-label-text">
            {{ t('CONVERSATION.ORDER_CARD.INVOICE_STATUS') }}
          </span>
          <span class="oc-value-text capitalize">
            {{ invoiceStatus }}
          </span>
        </p>
      </div>

      <div
        class="mt-2 mb-4 rounded-md border-0 border-t border-b border-[#F1F5F9] py-1.5 flex items-center justify-between gap-2"
      >
        <span class="oc-total-label">
          {{ t('CONVERSATION.ORDER_CARD.TOTAL') }}
        </span>
        <span class="text-sm font-semibold text-n-slate-12 break-words">
          {{ total }}
        </span>
      </div>

      <p class="oc-products-title m-0">
        {{ t('CONVERSATION.ORDER_CARD.PRODUCTS') }}
      </p>

      <div class="mt-2 grid grid-cols-2 gap-3">
        <div
          v-for="(product, index) in visibleProducts"
          :key="`${getProductSku(product)}-${index}`"
          class="p-3 border border-n-strong rounded-lg flex flex-col gap-1.5 !bg-[#F8FAFC] dark:!bg-n-solid-blue"
        >
          <p
            class="oc-single-product-name m-0"
            :title="getProductName(product)"
          >
            {{ getProductName(product) }}
          </p>
          <p
            v-if="getProductSku(product)"
            class="oc-single-product-sku m-0 flex gap-1"
          >
            <span
              class="oc-single-product-sku-label text-[11px] font-medium text-n-slate-10"
            >
              {{ t('CONVERSATION.ORDER_CARD.SKU') }}:
            </span>
            <span
              class="oc-single-product-sku-value text-[11px] text-n-slate-11"
            >
              {{ getProductSku(product) }}
            </span>
          </p>
          <div class="oc-single-product-meta flex flex-col gap-1">
            <div class="oc-single-product-meta-item flex gap-1">
              <span
                class="oc-single-product-meta-label text-[11px] font-medium text-n-slate-10"
              >
                {{ t('CONVERSATION.ORDER_CARD.QTY') }}:
              </span>
              <span
                class="oc-single-product-meta-value text-[11px] text-n-slate-11"
              >
                {{ getProductQty(product) }}
              </span>
            </div>
            <div class="oc-single-product-meta-item flex gap-1">
              <span
                class="oc-single-product-meta-label text-[11px] font-medium text-n-slate-10"
              >
                {{ t('CONVERSATION.ORDER_CARD.UNIT_PRICE') }}:
              </span>
              <span
                class="oc-single-product-meta-value text-[11px] text-n-slate-11"
              >
                {{ getProductUnitPrice(product) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="oc-show-more-wrap">
        <button
          v-if="hasMoreProducts"
          class="oc-single-products-toggle skip-context-menu"
          @click="toggleProducts"
        >
          {{
            isProductsExpanded
              ? t('CONVERSATION.ORDER_CARD.SHOW_LESS_PRODUCTS')
              : t('CONVERSATION.ORDER_CARD.SHOW_MORE_PRODUCTS', {
                  count: hiddenProductsCount,
                })
          }}
        </button>
      </div>
    </div>
    <div class="oc-label-text mt-4">
      {{ readableTime }}
    </div>
  </div>
</template>

<style scoped>
.oc-status-badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  text-transform: capitalize;
  white-space: nowrap;
}

.oc-status-badge.oc-status-sale {
  background-color: rgba(34, 197, 94, 0.1);
  color: #059669;
  border-radius: 4px;
  border: 1px solid #059669;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-draft {
  background-color: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-pending {
  background-color: #fefce8;
  color: #ca8a04;
  border: 1px solid #ca8a04;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-completed {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #1d4ed8;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-cancelled {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-default {
  background-color: rgba(209, 213, 219, 0.1);
  color: #4b5563;
}

.oc-single-product-name {
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  text-transform: capitalize;
  leading-trim: none;
  color: #475569;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark .oc-single-product-name {
  color: white;
}

.oc-single-product-sku-label,
.oc-single-product-meta-label {
  color: #475569;
  font-weight: 500;
}

.dark .oc-single-product-sku-label,
.dark .oc-single-product-meta-label {
  color: white;
}

.oc-single-product-sku-value,
.oc-single-product-meta-value {
  color: #64748b;
  font-weight: 400;
}

.dark .oc-single-product-sku-value,
.dark .oc-single-product-meta-value {
  color: white;
}

.oc-order-id {
  font-weight: 700;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #334155;
}

.dark .oc-order-id {
  color: white;
}

.oc-label-text {
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #64748b;
}

.dark .oc-label-text {
  color: white;
}

.oc-value-text {
  font-weight: 500;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #475569;
}

.dark .oc-value-text {
  color: white;
}

.oc-show-more-wrap {
  text-align: left;
  width: 100%;
}

.oc-single-products-toggle {
  font-weight: 500;
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  leading-trim: none;
  color: #1d4ed8;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  margin-top: 12px;
}

.oc-single-products-toggle:hover {
  text-decoration: underline;
}
</style>
